import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import {
  FolderOpen,
  SearchOff,
  Inventory2Outlined,
  WebAssetOff,
} from '@mui/icons-material';
import { colors } from '../../../ui/colors';

interface EmptyStateProps {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
  variant?: 'default' | 'search' | 'projects' | 'templates';
}

const DEFAULT_ICONS = {
  default: FolderOpen,
  search: SearchOff,
  projects: WebAssetOff,
  templates: Inventory2Outlined,
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  actionLabel,
  onAction,
  icon,
  variant = 'default',
}) => {
  // Get the icon component based on variant or use custom icon
  const DefaultIcon = DEFAULT_ICONS[variant];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 3, sm: 6 },
        minHeight: 320,
        textAlign: 'center',
      }}
    >
      {/* Icon Container */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: 'var(--fx-surface-muted, #F1F5F9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 3,
        }}
      >
        {icon ? (
          <Box sx={{ fontSize: 40, color: 'var(--fx-surface-texttertiary, #94A3B8)', display: 'flex' }}>
            {icon}
          </Box>
        ) : (
          <DefaultIcon
            sx={{
              fontSize: 40,
              color: 'var(--fx-surface-texttertiary, #94A3B8)',
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'var(--fx-surface-text, #0F172A)',
            fontSize: '18px',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'var(--fx-surface-textsecondary, #64748B)',
            fontSize: '14px',
            lineHeight: 1.6,
            maxWidth: 360,
            margin: '0 auto',
          }}
        >
          {description}
        </Typography>

        {/* Action Button */}
        {actionLabel && onAction && (
          <Box sx={{ marginTop: 2 }}>
            <Button
              variant="contained"
              onClick={onAction}
              sx={{
                backgroundColor: colors.accent || '#ff9500',
                color: '#ffffff',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                padding: '10px 24px',
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: colors.accentHover || '#e68900',
                  boxShadow: '0 4px 8px rgba(255, 149, 0, 0.2)',
                },
              }}
            >
              {actionLabel}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
